import memoize from 'fast-memoize'

import { BookingPaymentBalanceStatus } from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { mainColors, semanticColors, tapeChartColors } from '@avantstay/backoffice-core'

import { LegendOption } from '@new/domains/bookingsTapeChart/BookingsTapeChart.constants'

import {
  BlockedPeriodType,
  BookingsColorMode,
  EconRowType,
  TapeChartBookingsStatus,
} from '../__types'

export interface TapeColors {
  primary: string
  secondary: string
}

export const bookingPaymentColors: { [key: string]: TapeColors } = {
  [BookingPaymentBalanceStatus.fullyPaid]: {
    primary: tapeChartColors.fullyPaidPrimaryColor,
    secondary: tapeChartColors.fullyPaidSecondaryColor,
  },
  [BookingPaymentBalanceStatus.notPaid]: {
    primary: tapeChartColors.notPaidPrimaryColor,
    secondary: tapeChartColors.notPaidSecondaryColor,
  },
  [BookingPaymentBalanceStatus.overPaid]: {
    primary: tapeChartColors.overPaidPrimaryColor,
    secondary: tapeChartColors.overPaidSecondaryColor,
  },
  [BookingPaymentBalanceStatus.partlyPaid]: {
    primary: tapeChartColors.partiallyPaidPrimaryColor,
    secondary: tapeChartColors.partiallyPaidSecondaryColor,
  },
}

export const bookingStatusColors: { [key: string]: TapeColors } = {
  [TapeChartBookingsStatus.unassigned]: {
    primary: semanticColors.failureMedium,
    secondary: semanticColors.failureExtraLight,
  },
  [TapeChartBookingsStatus.unassignedOverlay]: {
    primary: mainColors.neutralDark,
    secondary: mainColors.primaryMedium,
  },
  [TapeChartBookingsStatus.booked]: {
    primary: mainColors.primaryMedium,
    secondary: mainColors.neutralDark,
  },
  [TapeChartBookingsStatus.checkedOut]: {
    primary: semanticColors.successMedium,
    secondary: semanticColors.successExtraLight,
  },
  [TapeChartBookingsStatus.checkedIn]: {
    primary: mainColors.accentDark,
    secondary: mainColors.accentExtremelyLight,
  },
}

const unassignedOverlayBooking: LegendOption<TapeChartBookingsStatus> = {
  label: 'Unassigned Overlay Booking',
  value: TapeChartBookingsStatus.unassignedOverlay,
  dot: bookingStatusColors[TapeChartBookingsStatus.unassignedOverlay].primary,
  borderColor: bookingStatusColors[TapeChartBookingsStatus.unassignedOverlay].secondary,
  borderStyle: 'dashed',
}

export const bookingReservationStatusOptions = (
  isUnassignedBookingsEnabled?: boolean,
): LegendOption<TapeChartBookingsStatus>[] => {
  const basReservationStatusOptions = [
    {
      label: 'Unassigned',
      value: TapeChartBookingsStatus.unassigned,
      dot: bookingStatusColors[TapeChartBookingsStatus.unassigned].primary,
    },
    {
      label: 'Checked-In',
      value: TapeChartBookingsStatus.checkedIn,
      dot: bookingStatusColors[TapeChartBookingsStatus.checkedIn].primary,
    },
    {
      label: 'Checked-Out',
      value: TapeChartBookingsStatus.checkedOut,
      dot: bookingStatusColors[TapeChartBookingsStatus.checkedOut].primary,
    },
    {
      label: 'Booked',
      value: TapeChartBookingsStatus.booked,
      dot: bookingStatusColors[TapeChartBookingsStatus.booked].primary,
    },
  ]

  return isUnassignedBookingsEnabled
    ? [...basReservationStatusOptions, unassignedOverlayBooking]
    : basReservationStatusOptions
}

export const bookingPaymentOptions: LegendOption<BookingPaymentBalanceStatus>[] = [
  {
    label: 'Fully Paid',
    value: BookingPaymentBalanceStatus.fullyPaid,
    dot: bookingPaymentColors[BookingPaymentBalanceStatus.fullyPaid].primary,
  },
  {
    label: 'Not Paid',
    value: BookingPaymentBalanceStatus.notPaid,
    dot: bookingPaymentColors[BookingPaymentBalanceStatus.notPaid].primary,
  },
  {
    label: 'Over Paid',
    value: BookingPaymentBalanceStatus.overPaid,
    dot: bookingPaymentColors[BookingPaymentBalanceStatus.overPaid].primary,
  },
]

export const colorModeOptions = [
  {
    label: 'Reservation status',
    value: BookingsColorMode.ReservationStatus,
  },
  {
    label: 'Payment status',
    value: BookingsColorMode.PaymentStatus,
  },
]

export const blockTypeOptions: LegendOption[] = [
  {
    rowStart: 2,
    label: 'Read-only block',
    value: BlockedPeriodType.ReadOnlyBlock,
    dot: mainColors.neutralExtraDark,
  },
  {
    rowStart: 2,
    label: 'Editable block',
    value: BlockedPeriodType.EditableBlock,
    dot: semanticColors.warningDark,
  },
]

export interface ColorsSet {
  frame: string
  background: string
  backgroundEven: string
  text: string
}

export interface ColorsConfig extends ColorsSet {
  hover?: Partial<ColorsSet>
}

export const rowColors: {
  [rowType in EconRowType]: {
    regular: ColorsSet
    hover: Partial<ColorsSet>
    selected: Partial<ColorsSet>
    selectedHover: Partial<ColorsSet>
  }
} = {
  [EconRowType.Normal]: {
    regular: {
      frame: 'transparent',
      background: mainColors.white,
      backgroundEven: mainColors.neutralExtraLight,
      text: mainColors.black,
    },
    hover: {
      background: mainColors.accentExtremelyLight,
      backgroundEven: mainColors.accentExtremelyLight,
      text: mainColors.accentMedium,
    },
    selected: {
      frame: mainColors.accentMedium,
      background: mainColors.accentExtremelyLight,
      backgroundEven: mainColors.accentExtremelyLight,
      text: mainColors.accentMedium,
    },
    selectedHover: {
      frame: mainColors.accentDark,
    },
  },
  [EconRowType.HighRisk]: {
    regular: {
      frame: semanticColors.failureMedium,
      background: tapeChartColors.highRiskPrimaryColor,
      backgroundEven: semanticColors.failureExtraLight,
      text: mainColors.black,
    },
    hover: {
      background: tapeChartColors.highRiskSecondaryColor,
      backgroundEven: tapeChartColors.highRiskSecondaryColor,
    },
    selected: {
      frame: semanticColors.failureMedium,
      background: tapeChartColors.highRiskSecondaryColor,
      backgroundEven: tapeChartColors.highRiskSecondaryColor,
    },
    selectedHover: {
      frame: semanticColors.failureDark,
    },
  },
  [EconRowType.OffBoarded]: {
    regular: {
      frame: mainColors.neutralDark,
      background: mainColors.neutralLight,
      backgroundEven: mainColors.neutralLight,
      text: mainColors.neutralDark,
    },
    hover: {
      background: mainColors.neutralMedium,
      backgroundEven: mainColors.neutralMedium,
      text: mainColors.black,
    },
    selected: {
      frame: mainColors.neutralExtraDark,
      background: mainColors.neutralMedium,
      backgroundEven: mainColors.neutralMedium,
      text: mainColors.black,
    },
    selectedHover: {
      frame: mainColors.neutralExtremelyDark,
    },
  },
}

interface RowColorsProps {
  type: EconRowType
  selected: boolean
  isListingSelectedForQuote: boolean
  disableHover?: boolean
}

export const getRowColors = memoize(
  ({
    type,
    selected,
    isListingSelectedForQuote,
    disableHover = false,
  }: RowColorsProps): ColorsConfig => {
    const colorsForType = rowColors[type]

    if (selected) {
      return {
        ...colorsForType.regular,
        ...colorsForType.selected,
        ...(!disableHover
          ? {
              hover: {
                ...colorsForType.selectedHover,
              },
            }
          : {}),
      }
    }

    if (isListingSelectedForQuote) {
      return {
        ...colorsForType.regular,
        ...colorsForType.selected,
        frame: 'transparent',
        ...(!disableHover
          ? {
              hover: {
                ...colorsForType.selectedHover,
              },
            }
          : {}),
      }
    }

    return {
      ...colorsForType.regular,
      ...(!disableHover
        ? {
            hover: {
              ...colorsForType.hover,
            },
          }
        : {}),
    }
  },
)
